import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Fuse from 'fuse.js';
import Drawer from '@material-ui/core/Drawer';
import Grid from '@material-ui/core/Grid';
import InfiniteScroll from 'react-infinite-scroller';

import PageLoader from 'components/PageLoader';
import ArchiveDialog from 'pages/Supplies/components/ArchiveDialog';
import NoResults from 'pages/Supplies/components/NoResults';
import {
  fetchArchived,
  restoreSupply,
  selectArchivedData,
  selectArchivedError,
  selectArchivedLoading,
  suppliesArchiveActions,
} from 'store/suppliesArchive';

const LIMIT = 20;

const ArchiveTab = ({ tab, onSelect, setSnackbar }) => {
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.personalProfile?.organization?.settings);
  const rows = useSelector((state) => selectArchivedData(state, tab.entity));
  const loading = useSelector((state) => selectArchivedLoading(state, tab.entity));
  const error = useSelector((state) => selectArchivedError(state, tab.entity));

  const [filters, setFilters] = useState(() => tab.defaultFilters(settings));
  const [searchValue, setSearchValue] = useState('');
  const [isFiltersOpen, setFiltersOpen] = useState(false);
  const [purging, setPurging] = useState(null);
  const [limit, setLimit] = useState(LIMIT);
  const [optionRows, setOptionRows] = useState([]);

  // filter options come only from an unfiltered load, so narrowing a filter keeps the other choices
  const refetch = useCallback(async () => {
    const data = await dispatch(fetchArchived(tab.entity, filters));
    if (data && tab.isUnfiltered?.(filters)) setOptionRows(data);
  }, [filters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!error) return;
    setSnackbar({ text: error, type: 'error' });
    dispatch(suppliesArchiveActions.errorCleared(tab.entity));
  }, [error]);

  const onRestore = useCallback(async (item) => {
    try {
      await dispatch(restoreSupply(tab.entity, item._id));
      setSnackbar({ text: 'Successfully Unarchived!', type: 'success' });
      refetch();
    } catch (e) {
      setSnackbar({ text: e.message, type: 'error' });
    }
  }, [refetch]);

  const closeFilters = useCallback(() => setFiltersOpen(false), []);

  const onUpdateFilters = useCallback((values) => {
    setFilters(values);
    closeFilters();
  }, []);

  const onCancelFilters = useCallback(() => {
    setFilters(tab.defaultFilters(settings));
    closeFilters();
  }, [settings]);

  const fuse = useMemo(() => new Fuse(rows, { shouldSort: true, keys: tab.searchKeys }), [rows]);
  const visibleRows = searchValue ? fuse.search(searchValue) : rows;
  const hasMore = tab.infinite && visibleRows.length > limit;

  const grid = (
    <Grid container spacing={2}>
      {(tab.infinite ? visibleRows.slice(0, limit) : visibleRows).map((item) => (
        <Grid key={item._id} item xs={12} md={6} lg={3}>
          <tab.Card
            data={item}
            onDelete={setPurging}
            onRestore={onRestore}
            onClick={() => onSelect(item)}
            {...(tab.cardProps ? tab.cardProps(settings) : {})}
          />
        </Grid>
      ))}
    </Grid>
  );

  // the loader only stands in for an empty list, so refetching after a restore keeps the toolbar in place
  return loading && !rows.length ? (
    <PageLoader loading>
      <div style={{ height: 100 }} />
    </PageLoader>
  ) : (
    <>
      <tab.FiltersBar
        openDrawer={() => () => setFiltersOpen(true)}
        updateSearchValue={setSearchValue}
        isDynamicSearch={true}
      />
      {tab.infinite ? (
        <InfiniteScroll
          pageStart={1}
          loadMore={() => setLimit((prev) => prev + LIMIT)}
          hasMore={hasMore}
          loader={<div key={0}>Loading ...</div>}
          threshold={300}
        >
          {grid}
        </InfiniteScroll>
      ) : (
        grid
      )}
      {!loading && !visibleRows.length && <NoResults />}
      <Drawer anchor="right" open={isFiltersOpen}>
        <tab.Panel
          closeDrawer={closeFilters}
          onCancel={onCancelFilters}
          onUpdate={onUpdateFilters}
          {...tab.panelProps(filters, optionRows, settings)}
        />
      </Drawer>
      {purging && (
        <ArchiveDialog
          entity={tab.entity}
          item={purging}
          action="purge"
          onClose={() => setPurging(null)}
          onDone={refetch}
          setSnackbar={setSnackbar}
        />
      )}
    </>
  );
};

export default ArchiveTab;
